import { useState, useEffect } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, CreditCard, XCircle, Clock, Filter } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { useSuperAdmin } from "@/hooks/use-super-admin";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PaymentWithBusiness {
  id: string;
  business_id: string;
  context: string;
  context_id: string | null;
  context_meta: any;
  payer_email: string;
  payer_name: string | null;
  currency: string;
  gross_amount: number;
  fee_amount: number;
  net_amount: number;
  status: "pending" | "success" | "failed" | "abandoned" | "refunded";
  payout_status: "not_applicable" | "pending" | "eligible" | "paid_out";
  provider_reference: string | null;
  initialized_at: string;
  paid_at: string | null;
  payout_marked_at: string | null;
  payout_reference: string | null;
  payout_notes: string | null;
  created_at: string;
  business?: {
    id: string;
    name: string;
    slug: string;
  } | null;
  // Enriched context data
  appointment?: {
    id: string;
    start_datetime: string;
    service?: { name: string } | null;
    customer?: { name: string } | null;
  } | null;
  invoice?: {
    id: string;
    invoice_number: string;
  } | null;
  membership?: {
    id: string;
    name: string;
  } | null;
}

export default function SuperAdminPayments() {
  const { isSuperAdmin } = useSuperAdmin();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [payments, setPayments] = useState<PaymentWithBusiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [payoutFilter, setPayoutFilter] = useState<string>("all");

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate("/app");
      return;
    }
    fetchPayments();
  }, [isSuperAdmin, navigate]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      
      const { data: paymentsData, error } = await supabase
        .from("platform_payments")
        .select(`
          *,
          business:businesses(id, name, slug)
        `)
        .order("created_at", { ascending: false })
        .limit(1000);

      if (error) throw error;

      // Enrich payments with context-specific data
      const enrichedPayments = await Promise.all(
        (paymentsData || []).map(async (payment) => {
          const enriched: any = { ...payment };

          // Fetch appointment data if context is appointment_deposit
          if (payment.context === "appointment_deposit" && payment.context_id) {
            const { data: appointment } = await supabase
              .from("appointments")
              .select(`
                id,
                start_datetime,
                service:services(name),
                customer:customers(name)
              `)
              .eq("id", payment.context_id)
              .maybeSingle();
            enriched.appointment = appointment;
          }

          // Fetch invoice data if context is invoice_payment
          if (payment.context === "invoice_payment" && payment.context_id) {
            const { data: invoice } = await supabase
              .from("invoices")
              .select("id, invoice_number")
              .eq("id", payment.context_id)
              .maybeSingle();
            enriched.invoice = invoice;
          }

          // Fetch membership data if context is membership_payment or membership_renewal
          if ((payment.context === "membership_payment" || payment.context === "membership_renewal") && payment.context_id) {
            const { data: membership } = await supabase
              .from("memberships")
              .select("id, name")
              .eq("id", payment.context_id)
              .maybeSingle();
            enriched.membership = membership;
          }

          return enriched;
        })
      );

      setPayments(enrichedPayments);
    } catch (error) {
      console.error("Error fetching payments:", error);
      toast({
        title: "Error",
        description: "Failed to fetch payments",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredPayments = payments.filter((payment) => {
    const matchesSearch =
      payment.business?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.payer_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.provider_reference?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || payment.status === statusFilter;
    const matchesPayout = payoutFilter === "all" || payment.payout_status === payoutFilter;

    return matchesSearch && matchesStatus && matchesPayout;
  });

  const eligiblePayments = filteredPayments.filter(
    p => p.status === "success" && (p.payout_status === "pending" || p.payout_status === "eligible")
  );

  const totalRevenue = filteredPayments
    .filter(p => p.status === "success")
    .reduce((sum, p) => sum + Number(p.gross_amount || 0), 0);

  const totalPendingPayouts = eligiblePayments.reduce((sum, p) => sum + Number(p.net_amount || 0), 0);

  const getPaymentDescription = (payment: PaymentWithBusiness): string => {
    switch (payment.context) {
      case "appointment_deposit":
        if (payment.appointment) {
          const serviceName = payment.appointment.service?.name || "Service";
          const date = payment.appointment.start_datetime
            ? format(new Date(payment.appointment.start_datetime), "MMM d, yyyy 'at' h:mm a")
            : "Unknown date";
          return `Appointment Deposit: ${serviceName} - ${date}`;
        }
        return "Appointment Deposit";
      
      case "invoice_payment":
        if (payment.invoice) {
          return `Invoice Payment: ${payment.invoice.invoice_number}`;
        }
        return "Invoice Payment";
      
      case "membership_payment":
        if (payment.membership) {
          return `Membership Payment: ${payment.membership.name}`;
        }
        return "Membership Payment";
      
      case "membership_renewal":
        if (payment.membership) {
          return `Membership Renewal: ${payment.membership.name}`;
        }
        return "Membership Renewal";
      
      case "day_pass_payment":
        return "Day Pass Payment";
      
      case "platform_subscription_setup":
        return "Platform Subscription Setup";

      case "platform_signup_fee":
        return "Business activation fee";

      case "appointment_reservation":
      case "appointment_group_reservation":
        return "Reservation fee (platform)";

      default:
        return payment.context.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getContextBadgeVariant = (context: string) => {
    switch (context) {
      case "appointment_deposit":
        return "default";
      case "invoice_payment":
        return "secondary";
      case "membership_payment":
        return "outline";
      case "day_pass_payment":
        return "outline";
      case "platform_subscription_setup":
        return "secondary";
      default:
        return "outline";
    }
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <AppShell businessName="Super Admin">
      <PageHeader
        title="All Payments"
        description="Ledger of all Paystack payments. Record business payouts on Settlements only."
        action={
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to="/app/platform/payouts">
                <CreditCard className="mr-2 h-4 w-4" />
                Settlements
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/app/super-admin">
                <CreditCard className="mr-2 h-4 w-4" />
                Dashboard
              </Link>
            </Button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-2 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₵{totalRevenue.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Payouts</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₵{totalPendingPayouts.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {eligiblePayments.length} payment(s) — pay via Settlements
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by business, payer email, or reference..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
        <Select value={payoutFilter} onValueChange={setPayoutFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Payout Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Payouts</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="eligible">Eligible</SelectItem>
            <SelectItem value="paid_out">Paid Out</SelectItem>
          </SelectContent>
        </Select>
        {eligiblePayments.length > 0 && (
          <Button asChild variant="default">
            <Link to="/app/platform/payouts">Open Settlements</Link>
          </Button>
        )}
      </div>

      {/* Payments Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Payments ({filteredPayments.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 font-medium">Business</th>
                    <th className="text-left p-4 font-medium">Payment For</th>
                    <th className="text-left p-4 font-medium">Payer</th>
                    <th className="text-right p-4 font-medium">Gross</th>
                    <th className="text-right p-4 font-medium">Fee</th>
                    <th className="text-right p-4 font-medium">Net</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Payout</th>
                    <th className="text-left p-4 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment) => {
                    return (
                      <tr
                        key={payment.id}
                        className="border-b hover:bg-muted/50"
                      >
                        <td className="p-4">
                          <div className="font-medium">{payment.business?.name || "N/A"}</div>
                          <div className="text-sm text-muted-foreground">/{payment.business?.slug || "N/A"}</div>
                        </td>
                        <td className="p-4">
                          <div className="space-y-1">
                            <Badge variant={getContextBadgeVariant(payment.context) as any}>
                              {payment.context === "appointment_deposit" && "Appointment Deposit"}
                              {payment.context === "invoice_payment" && "Invoice Payment"}
                              {payment.context === "membership_payment" && "Membership Payment"}
                              {payment.context === "membership_renewal" && "Membership Renewal"}
                              {payment.context === "day_pass_payment" && "Day Pass"}
                              {payment.context === "platform_subscription_setup" && "Subscription"}
                              {payment.context === "platform_signup_fee" && "Activation fee"}
                              {(payment.context === "appointment_reservation" ||
                                payment.context === "appointment_group_reservation") &&
                                "Reservation fee"}
                              {!["appointment_deposit", "invoice_payment", "membership_payment", "membership_renewal", "day_pass_payment", "platform_subscription_setup", "platform_signup_fee", "appointment_reservation", "appointment_group_reservation"].includes(payment.context) && payment.context}
                            </Badge>
                            <div className="text-sm text-muted-foreground max-w-xs">
                              {getPaymentDescription(payment)}
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <div>{payment.payer_name || "N/A"}</div>
                          <div className="text-sm text-muted-foreground">{payment.payer_email}</div>
                        </td>
                        <td className="p-4 text-right">₵{Number(payment.gross_amount || 0).toLocaleString()}</td>
                        <td className="p-4 text-right text-muted-foreground">₵{Number(payment.fee_amount || 0).toLocaleString()}</td>
                        <td className="p-4 text-right font-medium">₵{Number(payment.net_amount || 0).toLocaleString()}</td>
                        <td className="p-4">
                          <Badge
                            variant={
                              payment.status === "success"
                                ? "default"
                                : payment.status === "pending"
                                ? "secondary"
                                : "destructive"
                            }
                          >
                            {payment.status}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <Badge
                            variant={
                              payment.payout_status === "paid_out"
                                ? "default"
                                : payment.payout_status === "eligible"
                                ? "secondary"
                                : "outline"
                            }
                            className={
                              payment.payout_status === "paid_out"
                                ? "bg-green-600 hover:bg-green-700 text-white"
                                : payment.payout_status === "eligible"
                                ? "bg-green-100 hover:bg-green-200 text-green-800 dark:bg-green-900 dark:text-green-200"
                                : ""
                            }
                          >
                            {payment.payout_status === "paid_out" ? "Paid Out" : payment.payout_status === "eligible" ? "Eligible" : payment.payout_status}
                          </Badge>
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {payment.paid_at
                            ? format(new Date(payment.paid_at), "MMM d, yyyy")
                            : format(new Date(payment.created_at), "MMM d, yyyy")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

    </AppShell>
  );
}
